import { Injectable } from '@angular/core';
import { CoveragePremiumInterface } from '@app/core/interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { CalculationSelectorService } from './calculation-selector.service';
import { CalculationTransformService } from './calculation-transform.service';
import * as calculationActions from './calculation.actions';
import { CalculationService } from './calculation.service';

/** @deprecated use outshared-lib - InsurancePolicyActionService */
@Injectable()
export class CalculationEffects {

     calculateCoveragePremiumPurchasedProduct$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(calculationActions.LOAD_CALCULATION),
        map((action: calculationActions.LoadCalculation) => action.payload),
        mergeMap( ({ purchased_product_sequence_number }) =>
            this.calculationService.calculateCoveragePremiumPurchasedProduct({ purchased_product_sequence_number }).pipe(
                map( (calculation) => new calculationActions.LoadCalculationSuccess({ purchased_product_sequence_number, calculation })),
                catchError( (notifications) => of(new calculationActions.SetNotifications({ notifications }))),
            ),
        ),
    ));

     changeGenericPurchasedCoverages$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(calculationActions.CHANGE_GENERIC_PURCHASED_COVERAGES),
        map((action: calculationActions.ChangeGenericPurchasedCoverages) => action.payload),
        mergeMap(({ purchased_product_sequence_number, start_date, coverage_ids, product_id }) =>
            this.calculationService.changeGenericPurchasedCoverages({ purchased_product_sequence_number, start_date, coverage_ids, product_id }).pipe(
                withLatestFrom(this.calculationSelectorService.getAllCalculations$),
                map(([response, calculations]) => {
                    const foundCalculation: CoveragePremiumInterface = calculations
                        .find( (calculation) => calculation.purchased_product_sequence_number === purchased_product_sequence_number);
                    const { product } = foundCalculation;
                    const base_coverages = product.base_coverages ? product.base_coverages : [];
                    const additional_coverage = product.additional_coverage ? product.additional_coverage : [];

                    return [
                        ...base_coverages,
                        ...additional_coverage,
                    ];
                }),
                map((purchased_coverages) => this.calculationTransformService.mapCoveragesToFutureState({
                    purchased_coverages, start_date, coverage_ids,
                })),
                map((purchased_coverages) => new calculationActions.ChangeGenericPurchasedCoveragesSuccess({
                    purchased_coverages, start_date, purchased_product_sequence_number,
                })),
                catchError((notifications) => of(new calculationActions.SetNotifications({ notifications }))),
            ),
        ),
    ));

     cancelFuturePurchasedCoverage$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(calculationActions.CANCEL_FUTURE_PURCHASED_COVERAGE),
        map((action: calculationActions.CancelFuturePurchasedCoverage) => action.payload),
        mergeMap(({ purchased_product_sequence_number }) =>
            this.calculationService.cancelFuturePurchasedCoverage$({ purchased_product_sequence_number }).pipe(
                withLatestFrom(this.calculationSelectorService.getAllCalculations$),
                map(([response, calculations]) => {
                    const foundCalculation: CoveragePremiumInterface = calculations.find((calculation) =>
                        calculation.purchased_product_sequence_number === purchased_product_sequence_number,
                    );

                    const { product } = foundCalculation;
                    const baseCoverages = product.base_coverages ? product.base_coverages : [];
                    const additionalCoverage = product.additional_coverage ? product.additional_coverage : [];

                    return [
                        ...baseCoverages,
                        ...additionalCoverage,
                    ];
                }),
                map((purchasedCoverages) =>
                    this.calculationTransformService.mapCoveragesToCurrentState(purchasedCoverages),
                ),
                map((purchasedCoverages) => new calculationActions.CancelFuturePurchasedCoverageSuccess({
                    purchased_coverages: purchasedCoverages,
                    purchased_product_sequence_number,
                })),
                catchError((notifications) => of(new calculationActions.SetNotifications({ notifications }))),
            ),
        ),
    ));

    constructor(
        private actions$: Actions,
        private calculationService: CalculationService,
        private calculationTransformService: CalculationTransformService,
        private calculationSelectorService: CalculationSelectorService,
    ) {}
}
